import React, { useRef, MutableRefObject, useEffect, useState, useCallback } from "react"
import Editor, { monaco } from '@monaco-editor/react';
import { Select } from 'antd';
import ts from 'typescript';

// import { useToast } from '../helpers/commonFuction'
import { funcVaraible, Variables } from './typedefination';

const { Option } = Select;

export interface IProps {
    json?: any
    value?: any;
    editor?: any;
    height?: string;
    onSave?: ((data: any) => void) | null;
    theme?: string;
    type?: 'service' | 'func' | 'property';
    fontSize?: number;
    minimap?: boolean;
    lang: 'javascript' | 'typescript' | 'json'
    lineNumbers?: 'on' | 'off';
    readOnly?: boolean;
    customdef?: string
    editRangePhrase?: {
        start: string,
        end: string
    };
    saveMessage?: {
        enable: true,
        message: string
    } | {
        enable: false
    },
    editorCallback?: (cb: any) => void;
    hideLang?: boolean;
}

export default React.memo((props: IProps) => {

    const valueGetter: MutableRefObject<Function> = useRef(() => null);
    const [lang, setLang] = useState('javascript');
    const [count, setcount] = useState(0);


    // Phrases which define editable areas
    const startPhrase = (props.editRangePhrase && props.editRangePhrase.start) || '// Start of editable area'
    const endPhrase = (props.editRangePhrase && props.editRangePhrase.end) || '// End of editable area'

    const editableArea = ((startPhrase, endPhrase) => {
        return {
            includes: function (changes: any, model: any) {
                const fullRange = model.getFullModelRange();
                let { range: startRange } = model.findMatches(startPhrase, fullRange).shift() || {};
                let { range: endRange } = model.findMatches(endPhrase, fullRange).pop() || {};
                const {
                    startLineNumber,
                    endLineNumber,
                    startColumn,
                    endColumn
                } = fullRange;
                const isEmpty = (text: any) => text === ''; // ? refers to backspace and delete
                const isEnter = (text: any) => /\n/.test(text);
                if (startRange && endRange) {
                    startRange = startRange.setStartPosition(startLineNumber, startColumn);
                    endRange = endRange.setEndPosition(endLineNumber, endColumn);
                    return changes
                        .every(({ text, range }: { text: any, range: any }) => {
                            const specialCases = () => {
                                /*
                                ? This is done for my use case
                                ? This allows enter at the end of the start Range and 
                                ? This allows the enter and backspace on the start of the end Range
                                ? This is an optional case
                                */
                                return (
                                    (isEnter(text) || range.startLineNumber > startRange.endLineNumber) &&
                                    (isEnter(text) || isEmpty(text) || range.endLineNumber < endRange.startLineNumber)
                                );
                            }
                            return !startRange.strictContainsRange(range) &&
                                !endRange.strictContainsRange(range) &&
                                specialCases();

                        })
                }
                return false;
            }
        }
    })(startPhrase, endPhrase);

    function handleEditorDidMount(value: any, editor: any) {
        valueGetter.current = value;
        const editorModel = editor.getModel();
        editorModel.onDidChangeContentFast(({ changes, isUndoing }: { changes: any, isUndoing: any }) => {
            if (!isUndoing) {
                if (!editableArea.includes(changes, editorModel)) {
                    /*
                     * This Promise.resolve() sends the code to the micro task queue 
                     * And it gets called before the event queue ( micro task queue has more priority than event queue)
                     * Thus, Promise.resolve() will be better than a setTimeout(fn,0) here
                     * If we do it synchronously, it affects some of monaco editor's functions
                     */
                    Promise.resolve().then(() => editor.trigger('someIdString', 'undo'))
                }
            }
        })
    }

    // const toast = useToast()

    const { onSave, readOnly, saveMessage } = props;

    const compileAndSave = useCallback(async () => {
        const source = valueGetter.current();
        let compileSource;
        const monacoInstance = await monaco.init();
        const errors = monacoInstance.editor.getModelMarkers({}).filter((d) => d.resource.path !== 'customdefination/index.d.ts'
            && d.code !== "7044")
        if (errors.length > 0) {
            const shouldClose = onSave && onSave({ source: 'Error', comSource: 'Error' })
            return shouldClose
        }
        if (lang === 'typescript') {
            compileSource = ts.transpileModule(source, {
                compilerOptions: {
                    target: ts.ScriptTarget.ES2016,
                    module: ts.ModuleKind.CommonJS,
                    noEmit: true,
                }
            });
            if (compileSource.outputText) {
                const code = compileSource.outputText;
                const funIndex = code.indexOf('function main(');
                if (funIndex < 0) {
                    // toast({
                    //     type: 'error',
                    //     message: 'Main function not found'
                    // })
                    // e.preventDefault();
                    return 0;
                }
                compileSource = `async function(...params) {
                    ${code}
                    return main(...params)
                }`
            }
        }
        // compileSource = source;
        // }
        // saveMessage && saveMessage.enable &&
        // toast({
        //     type: 'success',
        //     message: saveMessage.message
        // })
        const shouldModalClose = onSave && onSave({ source: valueGetter.current(), comSource: compileSource })
        return shouldModalClose
    }, [lang, onSave])

    const keydownHandler = async (e: any) => {
        if ((e.key === 's' || e.key === 'S') && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            compileAndSave()
        }
    }

    useEffect(() => {
        if (props.editorCallback) {
            props.editorCallback(compileAndSave)
        }
    }, [compileAndSave])

    useEffect(() => {
        if (props.lang)
            setLang(props.lang!)
    }, [props.lang])

    useEffect(() => {
        if (onSave && !readOnly) {
            if (count === 0) {
                setcount(1);
                monaco
                    .init()
                    .then(monaco => {
                        // validation settings
                        monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
                            noSemanticValidation: false,
                            noSyntaxValidation: false,
                            noSuggestionDiagnostics: false
                        });
                        const options = {
                            // lib: ['ES2015', "DOM"],
                            target: monaco.languages.typescript.ScriptTarget.ES2016,
                            allowNonTsExtensions: true,
                            noImplicitAny: false
                            // noLib: true
                        }
                        // compiler options
                        monaco.languages.typescript.javascriptDefaults.setCompilerOptions(options);
                        monaco.languages.typescript.typescriptDefaults.setCompilerOptions(options);
                        // extra libraries
                        var libSource = [
                            '',
                        ];
                        if (props.type === 'func') libSource.push(funcVaraible)
                        if (props.type === 'service') libSource.push(Variables)
                        if (props.customdef) libSource.push(props.customdef)
                        var libUri = 'ts:customdefination/index.d.ts';
                        monaco.languages.typescript.javascriptDefaults.addExtraLib(libSource.join('\n'), libUri);

                        monaco.editor.createModel(libSource.join('\n'), 'typescript', monaco.Uri.parse(libUri));
                        monaco.editor.getModels().forEach(model => model.dispose());
                    })
                    .catch(error => console.error('An error occurred during initialization of Monaco: ', error));

            }
            document.addEventListener('keydown', keydownHandler)
            return () => {
                document.removeEventListener('keydown', keydownHandler)
            }
        }
    }, [onSave, count, lang, readOnly, saveMessage, props.type, props.customdef])
    return (
        <div>
            {lang && !props.hideLang && <div className={"row"} style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '24px' }}>
                <Select value={lang} style={{ width: 120 }} disabled={true}>
                    <Option value="javascript">JavaScript</Option>
                    <Option value="typescript">TypeScript</Option>
                    <Option value="json">JSON</Option>
                </Select>
            </div>}
            {/* <div id="codeEditor" style={{ height: '89vh' }}>

            </div> */}

            <Editor
                height={props.height}
                options={{
                    minimap: {
                        enabled: (props.minimap || false)
                    },
                    fontSize: props.fontSize,
                    lineNumbers: props.lineNumbers || 'on',
                    scrollBeyondLastLine: false,
                    readOnly: readOnly || false
                }}
                value={props.value}
                language={lang}
                theme={props.theme || 'light'}
                editorDidMount={handleEditorDidMount}
            />
        </div>

    )

})